import { useEffect, useMemo, useState } from "react";
import type {
  AdminLessonListItemDto,
  AdminRosterRelationsResponseDto,
  AdminStudentsResponseDto,
  EnrollmentsResponseDto,
  FamilyAccountsResponseDto,
  StudentStatus
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminLessons } from "../../shared/api/adminLesson";
import {
  bulkUpdateStudentStatus,
  fetchAdminEnrollments,
  fetchAdminRosterRelations,
  fetchAdminStudents,
  fetchFamilyAccounts
} from "../../shared/api/adminRoster";
import { adminSearchMatches } from "../../shared/lib/adminSearch";
import { buildCsvContent, downloadCsv, formatTenantDateForCsvFilename } from "../../shared/lib/csvExport";
import { getProtectedApiErrorMessage, isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { AdminBulkActionBar } from "../../shared/ui/admin-list/AdminBulkActionBar";
import { AdminDataGrid, type AdminDataGridColumn } from "../../shared/ui/admin-list/AdminDataGrid";
import { AdminFilterPanel } from "../../shared/ui/admin-list/AdminFilterPanel";
import { AdminListToolbar } from "../../shared/ui/admin-list/AdminListToolbar";
import { AdminPagination } from "../../shared/ui/admin-list/AdminPagination";
import { useAdminListState } from "../../shared/ui/admin-list/useAdminListState";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import {
  buildAdminRosterLinks,
  buildLessonFilterOptions,
  buildStudentRows,
  compareAdminDate,
  compareAdminText,
  formatAdminDate,
  formatGuardianNames,
  formatRosterPrimaryContact,
  summarizeNames,
  type AdminStudentListRow
} from "./AdminRosterListShared";

type StudentSortKey =
  | "displayName"
  | "displayNameKana"
  | "memberCode"
  | "familyAccountDisplayName"
  | "siblingCount"
  | "guardianCount"
  | "status"
  | "birthDate"
  | "updatedAt";

type StudentFilters = {
  status: "all" | StudentStatus;
  familyAccountId: "all" | "unset" | string;
  lessonId: "all" | string;
  guardian: "all" | "linked" | "unlinked";
  siblings: "all" | "has" | "none";
  primaryContact: "all" | "linkedToStudent" | "notLinkedToStudent" | "unset";
};

const studentSortKeys: StudentSortKey[] = [
  "displayName",
  "displayNameKana",
  "memberCode",
  "familyAccountDisplayName",
  "siblingCount",
  "guardianCount",
  "status",
  "birthDate",
  "updatedAt"
];

const defaultStudentFilters: StudentFilters = {
  status: "all",
  familyAccountId: "all",
  lessonId: "all",
  guardian: "all",
  siblings: "all",
  primaryContact: "all"
};

function statusPill(status: StudentStatus) {
  return <span className={`admin-list-pill admin-list-pill--${status}`}>{describeStatus(status)}</span>;
}

function compareStudentRows(left: AdminStudentListRow, right: AdminStudentListRow, sort: StudentSortKey): number {
  switch (sort) {
    case "displayName":
      return compareAdminText(left.student.displayName, right.student.displayName);
    case "displayNameKana":
      return compareAdminText(left.student.displayNameKana, right.student.displayNameKana);
    case "memberCode":
      return compareAdminText(left.student.memberCode, right.student.memberCode);
    case "familyAccountDisplayName":
      return compareAdminText(left.familyAccount?.displayName, right.familyAccount?.displayName);
    case "siblingCount":
      return left.siblingStudents.length - right.siblingStudents.length;
    case "guardianCount":
      return left.linkedGuardians.length - right.linkedGuardians.length;
    case "status":
      return compareAdminText(left.student.status, right.student.status);
    case "birthDate":
      return compareAdminDate(left.student.birthDate, right.student.birthDate);
    case "updatedAt":
      return compareAdminDate(left.student.updatedAt, right.student.updatedAt);
  }
}

function relationshipWarningBadge(warnings: readonly string[]) {
  if (warnings.length === 0) return null;

  return (
    <span className="admin-list-warning-badge" title={warnings.join("\n")} aria-label={`要確認: ${warnings.join("。")}`}>
      要確認
    </span>
  );
}

function memberStatusBadge(status: "active" | "inactive" | null) {
  if (status === "inactive") {
    return <span className="admin-list-chip admin-list-chip--warning">無効メンバー</span>;
  }

  if (status === null) {
    return <span className="admin-list-chip admin-list-chip--muted">メンバー未登録</span>;
  }

  return null;
}

function studentDetailHref(tenantSlug: string, studentId: string): string {
  return `/t/${tenantSlug}/teacher/admin/students/${studentId}`;
}

function familyAccountDetailHref(tenantSlug: string, familyAccountId: string): string {
  return `/t/${tenantSlug}/teacher/admin/family-accounts/${familyAccountId}`;
}

function renderStudentLink(tenantSlug: string, student: { studentId: string; displayName: string }) {
  return (
    <Link className="admin-list-chip" to={studentDetailHref(tenantSlug, student.studentId)}>
      {student.displayName}
    </Link>
  );
}

function renderStudentName(row: AdminStudentListRow, tenantSlug: string) {
  return (
    <div className="admin-list-relation">
      <strong>
        <Link to={studentDetailHref(tenantSlug, row.student.studentId)}>{row.student.displayName}</Link>
      </strong>
      {relationshipWarningBadge(row.dataQualityWarnings)}
    </div>
  );
}

function renderStudentFamily(row: AdminStudentListRow, tenantSlug: string) {
  const siblingSummary = summarizeNames(row.siblingStudents.map((student) => student.displayName), 2);

  return (
    <div className="admin-list-relation admin-list-relation--compact admin-list-relation--family">
      <div className="admin-list-relation__line">
        <span className="admin-list-relation__label">家族アカウント:</span>
        {row.familyAccount ? (
          <Link className="admin-list-chip" to={familyAccountDetailHref(tenantSlug, row.familyAccount.familyAccountId)}>
            {row.familyAccount.displayName}
          </Link>
        ) : (
          <span className="admin-list-chip admin-list-chip--muted" aria-label="家族アカウント未設定">未設定</span>
        )}
      </div>

      <div className="admin-list-relation__line">
        <span className="admin-list-relation__label">同じ家族:</span>
        {row.siblingStudents.length > 0 ? (
          <span className="admin-list-chip-list" title={siblingSummary.fullText}>
            {row.siblingStudents.slice(0, 2).map((student) => renderStudentLink(tenantSlug, student))}
            {row.siblingStudents.length > 2 ? (
              <span className="admin-list-chip admin-list-chip--muted">ほか{row.siblingStudents.length - 2}名</span>
            ) : null}
          </span>
        ) : (
          <span className="admin-list-chip admin-list-chip--muted" aria-label="同じ家族の生徒なし">なし</span>
        )}
      </div>
    </div>
  );
}

function renderStudentGuardians(row: AdminStudentListRow) {
  const guardianSummary = summarizeNames(row.linkedGuardians.map((guardian) => guardian.displayName), 2);

  return (
    <div className="admin-list-relation admin-list-relation--compact admin-list-relation--guardians">

      <div className="admin-list-relation__line">
        <span className="admin-list-relation__label">代表保護者:</span>
        <span className="admin-list-chip-list">
          <span
            className={`admin-list-chip${row.primaryContact && !row.primaryContact.hasActiveLinkToThisStudent ? " admin-list-chip--warning" : ""}`}
            title={
              row.primaryContact && !row.primaryContact.hasActiveLinkToThisStudent
                ? "代表保護者（連絡先）はこの生徒に紐づいていません"
                : undefined
            }
            aria-label={
              row.primaryContact && !row.primaryContact.hasActiveLinkToThisStudent
                ? `代表保護者はこの生徒に紐づいていません: ${formatRosterPrimaryContact(row.primaryContact)}`
                : undefined
            }
          >
            {row.primaryContact ? formatRosterPrimaryContact(row.primaryContact) : "未設定"}
          </span>
          {row.primaryContact ? memberStatusBadge(row.primaryContact.memberStatus) : null}
        </span>
      </div>

      <div className="admin-list-relation__line">
        <span className="admin-list-relation__label">アプリ閲覧保護者:</span>
        {row.linkedGuardians.length > 0 ? (
          <span className="admin-list-chip-list" title={guardianSummary.fullText}>
            {row.linkedGuardians.slice(0, 2).map((guardian) => (
              <span
                key={guardian.platformUserId}
                className={`admin-list-chip${guardian.memberStatus === "inactive" ? " admin-list-chip--warning" : ""}${guardian.memberStatus === null ? " admin-list-chip--muted" : ""}`}
                title={guardian.isPrimaryContact ? "代表保護者（連絡先）" : undefined}
              >
                {guardian.displayName}
                {guardian.isPrimaryContact ? "（代表）" : ""}
              </span>
            ))}
            {row.linkedGuardians.length > 2 ? (
              <span className="admin-list-chip admin-list-chip--muted">ほか{row.linkedGuardians.length - 2}名</span>
            ) : null}
          </span>
        ) : (
          <span className="admin-list-chip admin-list-chip--muted" aria-label="アプリ閲覧保護者なし">なし</span>
        )}
      </div>
    </div>
  );
}

export function AdminStudentsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentsResponseDto | null>(null);
  const [relations, setRelations] = useState<AdminRosterRelationsResponseDto | null>(null);
  const [lessons, setLessons] = useState<AdminLessonListItemDto[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const listState = useAdminListState<StudentSortKey, StudentFilters>({
    defaultSort: "displayName",
    defaultFilters: defaultStudentFilters,
    validSorts: studentSortKeys,
    validateFilter: (key, value) => {
      if (key === "status") return (value === "active" || value === "inactive" ? value : "all") as StudentFilters[typeof key];
      if (key === "familyAccountId") return (value || "all") as StudentFilters[typeof key];
      if (key === "lessonId") return (value || "all") as StudentFilters[typeof key];
      if (key === "guardian") return (value === "linked" || value === "unlinked" ? value : "all") as StudentFilters[typeof key];
      if (key === "siblings") return (value === "has" || value === "none" ? value : "all") as StudentFilters[typeof key];
      if (key === "primaryContact") {
        return (
          value === "linkedToStudent" || value === "notLinkedToStudent" || value === "unset" ? value : "all"
        ) as StudentFilters[typeof key];
      }
      return "all" as StudentFilters[typeof key];
    }
  });

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextStudents, nextFamilyAccounts, nextEnrollments, nextLessons, nextRelations] = await Promise.all([
        fetchAdminStudents(tenantSlug),
        fetchFamilyAccounts(tenantSlug),
        fetchAdminEnrollments(tenantSlug),
        fetchAdminLessons(tenantSlug),
        fetchAdminRosterRelations(tenantSlug)
      ]);

      setStudents(nextStudents);
      setFamilyAccounts(nextFamilyAccounts);
      setEnrollments(nextEnrollments);
      setLessons(nextLessons.items);
      setRelations(nextRelations);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.student}一覧の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  const allRows = useMemo(
    () =>
      buildStudentRows({
        students: students?.items ?? [],
        familyAccounts: familyAccounts?.items ?? [],
        enrollments: enrollments?.items ?? [],
        relations
      }),
    [enrollments, familyAccounts, relations, students]
  );

  const filteredRows = useMemo(() => {
    const filters = listState.filters;
    return allRows.filter((row) => {
      if (!adminSearchMatches(listState.appliedQuery, row.searchText)) return false;
      if (filters.status !== "all" && row.student.status !== filters.status) return false;
      if (filters.familyAccountId === "unset" && row.student.familyAccountId) return false;
      if (filters.familyAccountId !== "all" && filters.familyAccountId !== "unset" && row.student.familyAccountId !== filters.familyAccountId) return false;
      if (filters.lessonId !== "all" && !row.currentLessonIds.includes(filters.lessonId)) return false;
      if (filters.guardian === "linked" && row.linkedGuardians.length === 0) return false;
      if (filters.guardian === "unlinked" && row.linkedGuardians.length > 0) return false;
      if (filters.siblings === "has" && row.siblingStudents.length === 0) return false;
      if (filters.siblings === "none" && row.siblingStudents.length > 0) return false;
      if (filters.primaryContact === "linkedToStudent" && !row.primaryContact?.hasActiveLinkToThisStudent) return false;
      if (filters.primaryContact === "notLinkedToStudent" && (!row.primaryContact || row.primaryContact.hasActiveLinkToThisStudent)) return false;
      if (filters.primaryContact === "unset" && row.primaryContact) return false;
      return true;
    });
  }, [allRows, listState.appliedQuery, listState.filters]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((left, right) => {
      const result = compareStudentRows(left, right, listState.sort) || compareAdminText(left.student.studentId, right.student.studentId);
      return listState.order === "asc" ? result : -result;
    });
  }, [filteredRows, listState.order, listState.sort]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / listState.pageSize));
  const currentPage = Math.min(listState.page, pageCount);
  const pagedRows = sortedRows.slice((currentPage - 1) * listState.pageSize, currentPage * listState.pageSize);
  const filteredIds = new Set(filteredRows.map((row) => row.student.studentId));
  const hiddenSelectedCount = [...selectedIds].filter((id) => !filteredIds.has(id)).length;
  const selectedRows = allRows.filter((row) => selectedIds.has(row.student.studentId));
  const exportRows = selectedRows.length > 0 ? selectedRows : filteredRows;
  const lessonOptions = buildLessonFilterOptions(lessons);

  useEffect(() => {
    if (listState.page !== currentPage) listState.setPage(currentPage);
  }, [currentPage, listState]);

  function toggleCurrentPageSelection() {
    setSelectedIds((current) => {
      const next = new Set(current);
      const currentPageIds = pagedRows.map((row) => row.student.studentId);
      const allSelected = currentPageIds.every((id) => next.has(id));
      for (const id of currentPageIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  async function handleBulkStatus(status: StudentStatus) {
    const count = selectedIds.size;
    if (count === 0) return;
    const label = status === "active" ? "有効" : "無効";
    const confirmed = window.confirm(
      `選択した${count}名の${tenantLabels.student}を「${label}」に変更します。\n所属や保護者との紐付けは変更されません。`
    );

    if (!confirmed) return;

    setBulkSaving(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await bulkUpdateStudentStatus(tenantSlug, { studentIds: [...selectedIds], status });
      setSelectedIds(new Set());
      setFeedbackMessage(`選択した${count}名の${tenantLabels.student}を更新しました。`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadError(getProtectedApiErrorMessage(error, "一括ステータス更新に失敗しました"));
    } finally {
      setBulkSaving(false);
    }
  }

  function handleExportCsv() {
    const content = buildCsvContent({
      rows: exportRows,
      columns: [
        { header: "会員コード", value: (row) => row.student.memberCode },
        { header: "姓", value: (row) => row.student.familyName },
        { header: "名", value: (row) => row.student.givenName },
        { header: "表示名", value: (row) => row.student.displayName },
        { header: "表示名かな", value: (row) => row.student.displayNameKana },
        { header: "生年月日", value: (row) => row.student.birthDate },
        { header: "ステータス", value: (row) => describeStatus(row.student.status) },
        { header: tenantLabels.familyAccount, value: (row) => row.familyAccount?.displayName ?? "" },
        { header: `同じ家族の${tenantLabels.student}`, value: (row) => row.siblingStudents.map((student) => student.displayName).join("／") },
        { header: "代表保護者（連絡先）", value: (row) => row.primaryContact?.displayName ?? "" },
        { header: "アプリで閲覧できる保護者", value: (row) => formatGuardianNames(row.linkedGuardians) },
        { header: `${tenantLabels.enrollment}中${tenantLabels.lesson}`, value: (row) => row.currentLessonTitles.join("／") },
        { header: "作成日時", value: (row) => row.student.createdAt },
        { header: "更新日時", value: (row) => row.student.updatedAt }
      ]
    });
    downloadCsv(`${tenantSlug}_students_${formatTenantDateForCsvFilename()}.csv`, content);
  }

  const columns: Array<AdminDataGridColumn<AdminStudentListRow>> = [
    { key: "displayName", header: tenantLabels.student, sortable: true, mobilePriority: "primary", render: (row) => renderStudentName(row, tenantSlug) },
    { key: "memberCode", header: "会員コード", sortable: true, width: "120px", mobilePriority: "secondary", render: (row) => row.student.memberCode },
    { key: "familyAccountDisplayName", header: "家族", sortable: true, width: "250px", mobilePriority: "secondary", render: (row) => renderStudentFamily(row, tenantSlug) },
    { key: "siblingCount", header: "同家族数", sortable: true, width: "88px", align: "center", mobilePriority: "hidden", render: (row) => row.siblingStudents.length },
    { key: "guardians", header: "保護者", sortable: false, width: "250px", mobilePriority: "secondary", render: renderStudentGuardians },
    { key: "guardianCount", header: "閲覧保護者数", sortable: true, width: "112px", align: "center", mobilePriority: "hidden", render: (row) => row.linkedGuardians.length },
    { key: "currentLessons", header: `所属中${tenantLabels.lesson}`, mobilePriority: "secondary", render: (row) => row.currentLessonTitles.join(" / ") || "なし" },
    { key: "status", header: "ステータス", sortable: true, width: "110px", mobilePriority: "primary", render: (row) => statusPill(row.student.status) },
    { key: "birthDate", header: "生年月日", sortable: true, width: "120px", mobilePriority: "hidden", render: (row) => row.student.birthDate || "未設定" },
    { key: "updatedAt", header: "更新日時", sortable: true, width: "150px", mobilePriority: "hidden", render: (row) => formatAdminDate(row.student.updatedAt) }
  ];

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.student}一覧`}
      description={`${tenantLabels.student}を検索・絞り込み・一括更新できます。登録は専用ページで行います。`}
      links={buildAdminRosterLinks(tenantLabels)}
      layoutVariant="wide"
    >
      <div className="admin-list-page">
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <AdminListToolbar
              title={`${tenantLabels.student}一覧`}
              totalCount={filteredRows.length}
              draftQuery={listState.draftQuery}
              appliedQuery={listState.appliedQuery}
              searchPlaceholder={`氏名・かな・会員コード・家族・同じ家族・代表連絡先・閲覧保護者・${tenantLabels.lesson}で検索`}
              csvButtonLabel={selectedIds.size > 0 ? "選択中をCSV出力" : "絞り込み結果をCSV出力"}
              csvDisabled={loadState === "loading" || exportRows.length === 0}
              createHref={`/t/${tenantSlug}/teacher/admin/students/new`}
              createLabel={`${tenantLabels.student}を登録`}
              onDraftQueryChange={listState.setDraftQuery}
              onSearchSubmit={listState.submitSearch}
              onExportCsv={handleExportCsv}
            >
              <AdminFilterPanel>
                <label>
                  <span>ステータス</span>
                  <select value={listState.filters.status} onChange={(event) => listState.setFilter("status", event.target.value as StudentFilters["status"])}>
                    <option value="all">すべて</option>
                    <option value="active">{describeStatus("active")}</option>
                    <option value="inactive">{describeStatus("inactive")}</option>
                  </select>
                </label>
                <label>
                  <span>{tenantLabels.familyAccount}</span>
                  <select value={listState.filters.familyAccountId} onChange={(event) => listState.setFilter("familyAccountId", event.target.value)}>
                    <option value="all">すべて</option>
                    <option value="unset">未設定</option>
                    {familyAccounts?.items.map((familyAccount) => (
                      <option key={familyAccount.familyAccountId} value={familyAccount.familyAccountId}>{familyAccount.displayName}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{tenantLabels.lesson}</span>
                  <select value={listState.filters.lessonId} onChange={(event) => listState.setFilter("lessonId", event.target.value)}>
                    <option value="all">すべて</option>
                    {lessonOptions.map((lesson) => (
                      <option key={lesson.lessonId} value={lesson.lessonId}>{lesson.title}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>アプリ閲覧保護者</span>
                  <select value={listState.filters.guardian} onChange={(event) => listState.setFilter("guardian", event.target.value as StudentFilters["guardian"])}>
                    <option value="all">すべて</option>
                    <option value="linked">あり</option>
                    <option value="unlinked">なし</option>
                  </select>
                </label>
                <label>
                  <span>同じ家族</span>
                  <select value={listState.filters.siblings} onChange={(event) => listState.setFilter("siblings", event.target.value as StudentFilters["siblings"])}>
                    <option value="all">すべて</option>
                    <option value="has">あり</option>
                    <option value="none">なし</option>
                  </select>
                </label>
                <label>
                  <span>代表保護者（連絡先）の紐づけ</span>
                  <select value={listState.filters.primaryContact} onChange={(event) => listState.setFilter("primaryContact", event.target.value as StudentFilters["primaryContact"])}>
                    <option value="all">すべて</option>
                    <option value="linkedToStudent">この生徒に紐づきあり</option>
                    <option value="notLinkedToStudent">この生徒に紐づきなし</option>
                    <option value="unset">代表保護者（連絡先）未設定</option>
                  </select>
                </label>
              </AdminFilterPanel>
            </AdminListToolbar>

            <AdminBulkActionBar
              selectedCount={selectedIds.size}
              hiddenSelectedCount={hiddenSelectedCount}
              saving={bulkSaving}
              disabled={loadState === "loading"}
              activeLabel="有効にする"
              inactiveLabel="無効にする"
              onBulkStatus={handleBulkStatus}
              onClearVisibleSelection={() => setSelectedIds((current) => new Set([...current].filter((id) => !filteredIds.has(id))))}
              onClearAllSelection={() => setSelectedIds(new Set())}
            />

            <AdminDataGrid
              columns={columns}
              rows={pagedRows}
              getRowId={(row) => row.student.studentId}
              getRowLabel={(row) => row.student.displayName}
              getDetailHref={(row) => studentDetailHref(tenantSlug, row.student.studentId)}
              selectedIds={selectedIds}
              sortKey={listState.sort}
              sortOrder={listState.order}
              loading={loadState === "loading"}
              errorMessage={loadState === "error" ? loadError : null}
              emptyMessage={`${tenantLabels.student}は見つかりませんでした。`}
              onRetry={() => void refreshPage()}
              onToggleRow={(rowId) =>
                setSelectedIds((current) => {
                  const next = new Set(current);
                  if (next.has(rowId)) next.delete(rowId);
                  else next.add(rowId);
                  return next;
                })
              }
              onToggleCurrentPage={toggleCurrentPageSelection}
              onSort={(columnKey) => listState.setSort(columnKey as StudentSortKey)}
            />

            <AdminPagination
              page={currentPage}
              pageSize={listState.pageSize}
              totalCount={filteredRows.length}
              onPageChange={listState.setPage}
              onPageSizeChange={listState.setPageSize}
            />
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
